<template>
  <div class="main">
    <div class="d-flex justify-content-center" v-if="loading">
      <b-spinner variant="success" label="Spinning" style="width: 3rem; height: 3rem"></b-spinner>
    </div>
    <b-row v-if="!loading">
      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button variant="primary" to="/servicetimesheet/add" class="my-1">
            Add Timesheet
          </b-button>
        </div>
      </b-col>
      <b-col  cols="12" md="2" sm="2" class="my-1">
        <b-form-group class="mb-0">
          <label class="d-inline-block text-sm-left mr-50">Per page</label>
          <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions" />
        </b-form-group>
      </b-col>
    

      <b-col sm="10" class="my-1">
        <div class="d-flex justify-content-end">
          <b-button variant="outline-danger" @click="clearFilters" :disabled="!anyFilterActive">
            Clear Filters
          </b-button>
        </div>
      </b-col>

      <b-col cols="12">
        <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredTimesheets"
          :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @sort-changed="onSortChanged"
          @filtered="onFiltered">
          
          <template #head(employeeID)>
            <div class="d-flex flex-column">
              <span>User</span>
              <b-form-input
                v-model="columnFilters.employeeName"
                placeholder="Filter by User"
                size="sm"
                @input="filterTable"
              />
            </div>
          </template>
          
          
          <template #head(reference)>
            <div class="d-flex flex-column">
              <span>Reference</span>
              <b-form-input v-model="columnFilters.reference" placeholder="Filter by Reference" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #head(date)="scope">
            <div class="column-header">
              <strong @click="toggleSort('date')">Appointment Date</strong>
              <date-picker v-model="columnFilters.date" :range="true" format="YYYY-MM-DD"
                placeholder="Select Date Range" @change="filterTable" class="mt-1 w-100" :clearable="false"
                @clear.prevent="preventClear" :input-attr="{ readonly: true }" @focus="openCalendar" />
            </div>
          </template>

          <template #head(status)>
            <div class="d-flex flex-column">
              <span>Status</span>
              <b-form-select v-model="columnFilters.status" :options="statusOptions" @change="filterTable" size="sm"
                class="mt-1" placeholder="Select Status">
                <!-- Placeholder Option -->
                <!-- <option :value="null" disabled>Select Status</option> -->
              </b-form-select>
            </div>
          </template>


          <!-- Approval Date Column with Range Picker -->

          <template #head(approvalDate)="scope">
            <div class="column-header">
              <strong @click="toggleSort('approvalDate')">Approval Date</strong>
              <date-picker v-model="columnFilters.approvalDate" :range="true" format="YYYY-MM-DD"
                placeholder="Select Approval Date Range" @change="filterTable" class="mt-1 w-100" :clearable="false"
                @clear.prevent="preventClear" :input-attr="{ readonly: true }" @focus="openCalendar" />
            </div>
          </template>

          <template #cell(employeeID)="data">
                {{ getEmployee(data.item.employeeID) }}
              </template>

          <template #cell(actions)="data">
            <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
              <template v-slot:button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
              </template>
              <b-dropdown-item 
                    v-if="checkPermissions(data.item)" 
                    @click="timesheetAction('APPROVED', data.item)">
                    <feather-icon icon="CheckIcon" />
                    <span class="align-middle ml-50">Approve</span>
                  </b-dropdown-item>

                  <b-dropdown-item 
                    v-if="checkPermissions(data.item)" 
                    @click="timesheetAction('REJECTED', data.item)">
                    <feather-icon icon="XIcon" />
                    <span class="align-middle ml-50">Reject</span>
                  </b-dropdown-item>
              <b-dropdown-item :to="`/servicetimesheet/view/${data.item.id}`">
                <feather-icon icon="EyeIcon" class="mr-50" />
                <span>View</span>
              </b-dropdown-item>
              <!-- {{ userId }} -->
              <b-dropdown-item v-if="data.item.status === 'PENDING' && data.item.approverID === userId"
                :to="`/servicetimesheet/edit/${data.item.id}`">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Update</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <div class="d-flex justify-content-center mb-1">
          <b-alert variant="warning" show v-if="timesheets.length == 0" class="d-flex justify-content-center">
            <h4 class="alert-heading">No items found</h4>
          </b-alert>
        </div>
        <div class="d-flex justify-content-center mb-1">
          <b-spinner variant="success" label="Spinning" block v-if="loading"
            style="width: 3rem; height: 3rem"></b-spinner>
        </div>
      </b-col>

      <b-col cols="12">
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
              class="mt-2" />
              <b-form-group>
        Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
      </b-form-group>
          </b-col>
    </b-row>

  </div>
</template>

<script>
import DatePicker from "vue2-datepicker"; // Import the date picker component
import "vue2-datepicker/index.css"; // Import the date picker styles
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BAlert,
  BDropdown,
  BDropdownItem,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { formatDate } from "@/libs/customFn";
import { TimesheetStatus } from '@/libs/defaultList';
import { IsAdmin } from "@/@core/utils/validations/permissionCheck";

export default {
  components: {
    DatePicker,
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    BAlert,
    BSpinner,
    formatDate,
  },
  data() {
    return {
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      // filter: null,
      columnFilters: {
        reference: "",
        date: null,
        status: null,
        approvalDate: null,
        employeeName: "",
      },
      fields: [
      { key: "employeeID", label: "User", sortable: true, filter: true},
        { key: "reference", label: "Reference", sortable: true },
        { key: "date", label: "Date", sortable: true, formatter: (value, key, item) => formatDate(item.date) },
        {
          key: "status",
          label: "Status",
          sortable: true,
          formatter: (value, key, item) =>
            this.timesheetStatus.find((x) => x.value === item.status)?.text || "",
        },
        {
          key: "approvalDate",
          label: "Approval Date",
          sortable: true,
          formatter: (value, key, item) =>
            item.approvalDate ? formatDate(item.approvalDate) : "",
        },
        { key: "actions", label: "Actions", sortable: false },
      ],
      statusOptions: [
        { value: null, text: "Select Status" },
        { value: "PENDING", text: "Pending" },
        { value: "APPROVED", text: "Approved" },
        { value: "REJECTED", text: "Rejected" },
      ],
      userId: null,
      timesheetStatus: TimesheetStatus,
      moduleReference: "ServiceOrder",
      timesheets:[],
    };
  },
  methods: {
    ...mapActions("serviceModule", ["getServiceTimesheetListAction", "getServiceTimesheetAction", "putServiceTimesheetAction"]),
    ...mapActions("employeeModule", ["getEmployeeListAction"]),
    ...mapActions("userModule", ["getUserListAction"]),
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    filterTable() {
      this.currentPage = 1; // Reset to the first page
      this.totalRows = this.filteredTimesheets.length; // Update total rows
    },
    // Utility to check if a date falls within a given range
    isWithinRange(date, range) {
      if (!date || !range || range.length !== 2) return false;
      const [start, end] = range.map((d) => new Date(d));
      const itemDate = new Date(date);
      return itemDate >= start && itemDate <= end;
    },
    clearFilters() {
      this.columnFilters = {
        reference: "",
        date: null,
        status: "",
        approvalDate: null,
      };
      this.filterTable(); // Reapply filters and reset pagination
    },
    preventClear(event) {
      // Prevent the clear event or log an error if needed
      event.preventDefault();
    },
    openCalendar(event) {
      // Ensure the calendar opens on focus
      event.target.blur(); // Remove focus to prevent further typing
      event.target.click(); // Programmatically open the calendar

      // Add an event listener to detect clicks outside the date picker
      document.addEventListener('click', this.closeCalendar);
    },
    
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    closeCalendar(event) {
      const datePicker = this.$refs.datePicker.$el;

      // Check if the click happened outside the date picker
      if (!datePicker.contains(event.target)) {
        // Close the calendar by clicking outside
        this.$refs.datePicker.hideCalendar();

        // Remove the event listener after closing
        document.removeEventListener('click', this.closeCalendar);
      }
    },

    async timesheetAction(status, timesheet) {
      try {
        await this.getServiceTimesheetAction({modulereference: this.moduleReference, id: timesheet.id});

        // confirm update
        const result = await this.$swal({
          title: "Are you sure?",
          text: "You will not be able to revert your changes!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, update status!",
          cancelButtonText: "Cancel",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });

        if (result.isConfirmed) {
          const payload = {
            id: timesheet.id,
            approverID: timesheet.approverID,
            status: status,
            note: status,
            moduleReference: this.moduleReference
          };

          console.log("payload", payload);

          try {
            await this.putServiceTimesheetAction(payload)
            .then(() => {
              this.success();

              const modulereference = 'ServiceOrder';
               //this.fetchTimesheetData();
               const userData = JSON.parse(localStorage.getItem("userData"));
    this.userId = userData.id;
               const emp = this.getEmployeeUser(this.userId);
      this.getServiceTimesheetListAction(modulereference).then(() => {
      if(IsAdmin()){
        
        this.timesheets = this.timesheetList;
        this.totalRows = this.timesheetList.length;
      }
      else {
        this.getServiceTimesheetListAction(modulereference).then(() => {
        this.timesheets = this.timesheetList.filter(x => x.employeeID == emp || x.approverID == emp);
        console.log("this.timesheets", this.timesheets);
        this.totalRows = this.timesheets.length;
      });
      }
     });
          });
            
          } catch (updateError) {
            console.error("Error updating timesheet:", updateError);
            await this.$swal({
              title: "Error",
              text: "An error occurred while updating the timesheet.",
              icon: "error",
              customClass: {
                confirmButton: "btn btn-danger",
              },
            });
          }
        }
      } catch (error) {
        console.error("Error fetching timesheet:", error);
        await this.$swal({
          title: "Error",
          text: "An error occurred while fetching the timesheet data.",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-danger",
          },
        });
      }
    },

    success(message) {
      this.$swal({
        title: "Timesheet Status Updated!",
        text: message,
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    getEmployee(id) {
      const employee = this.users.find((s) => s.id === id);
      return employee ? employee.firstName + " " + employee.lastName : "-";
    },

    getEmployeeUser(id){
    //console.log("iddddddddddddddddddddddddddddddddddd", id);
    const employee = this.employeeList.find((s) => s.refId === id);
    //console.log("employeeeeeeeeeeeeeeeeeeeeeee", employee);
    return employee.id;
  },

    checkPermissions(item) {
    console.log("Item Status:", item.status);
    console.log("Module Type:", item.moduleType, "Expected:", this.moduleReference);
    console.log("User is Admin:", IsAdmin());
    console.log("User ID:", this.userId, "Approver ID:", item.approverID);

    return item.status === 'PENDING' 
      && (IsAdmin() || this.userId === item.approverID);
  },
  },
  computed: {
    ...mapGetters("serviceModule", {
      timesheetList: "timesheetList", // Ensure this gets the list correctly from Vuex
      loading: "loading",
    }),
    ...mapGetters("employeeModule", {
      employeeList: "employees",
      loading: "loading",
    }),
    ...mapGetters("userModule", {
      users: "users",
      loading: "loading",
    }),
    filteredTimesheets() {
      console.log('Filtering with:', this.columnFilters); // Debugging output

      console.log('dssd:', this.timesheets); 


      return this.timesheets.filter((item) => {
        const matchesEmployee = !this.columnFilters.employeeName ||
  this.getEmployee(item.employeeID).toLowerCase().includes(this.columnFilters.employeeName.toLowerCase());
        
        const matchesReference = !this.columnFilters.reference ||
          String(item.reference || "").toLowerCase().includes(this.columnFilters.reference.toLowerCase());

        const matchesStatus = !this.columnFilters.status ||
          item.status.toLowerCase().includes(this.columnFilters.status.toLowerCase());

        const matchesDate = !this.columnFilters.date ||
          this.isWithinRange(item.date, this.columnFilters.date);

        const matchesApprovalDate = !this.columnFilters.approvalDate ||
          this.isWithinRange(item.approvalDate, this.columnFilters.approvalDate);

        return matchesReference && matchesStatus && matchesDate && matchesApprovalDate &&  matchesEmployee;
      });
    },
   
    anyFilterActive() {
      const { reference, date, status, approvalDate } = this.columnFilters;
      return (
        reference || status || (date && date.length) || (approvalDate && approvalDate.length)
      );
    },
  },
  async mounted() {
    const modulereference = 'ServiceOrder';

    await this.getUserListAction();

const userData = JSON.parse(localStorage.getItem("userData"));
    this.userId = userData.id;
     await this.getServiceTimesheetListAction(modulereference).then(() => {
      if(IsAdmin()){
        
        this.timesheets = this.timesheetList;
        this.totalRows = this.timesheetList.length;
      }
      else {
       // console.log("employeeeeeeeeeeeeeManagerrrrrrrrrrrrrrrrrrrr");
        this.getServiceTimesheetListAction(modulereference).then(() => {
        this.timesheets = this.timesheetList.filter(x => x.employeeID == this.userId || x.approverID == this.userId);
        console.log("this.timesheets", this.timesheets);
        this.totalRows = this.timesheets.length;
      });
      }
     });
//console.log("list", this.timesheetList);
   
  }

};
</script>
